import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, useMediaQuery, IconButton, Button } from '@mui/material';
import { Search, Bookmark } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { en } from '../translations/en';
import { AppButton } from '../components/AppButton';
import { useBookmarks } from '../hooks/useBookmarks';
import { usePermissions } from '../hooks/usePermissions';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { buildArtworkDetailLink } from '../utils/artworkDetailLink';
import { getCardImageUrl, getArtworkTitle, getPublicCatalogItem, getOwnCatalogItem, Artwork, PrivateArtworkError } from '../utils/artworkApi';
import { getCreatorDisplayName } from '../utils/stringHelpers';
import { ThumbnailImage } from '../components/ThumbnailImage';
import { logger } from '../utils/logger';

export const Bookmarks = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLaptop = useMediaQuery(theme.breakpoints.down('lg'));
  const { bookmarks, toggleBookmark } = useBookmarks();
  const { permissions } = usePermissions();
  const [bookmarkedArtworks, setBookmarkedArtworks] = useState<Artwork[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch each bookmarked item individually — avoids loading the full catalog (GET /api/catalogs).
  // Strategy:
  //   1. Try getPublicCatalogItem — works for public items (own or cross-catalog)
  //   2. On PrivateArtworkError — try getOwnCatalogItem (authenticated single-item fetch)
  //      for the case where the user bookmarked their own private artwork
  //   3. Items that return null (deleted or truly inaccessible) are silently skipped
  useEffect(() => {
    let cancelled = false;

    // Fetch all bookmarked items individually — avoids loading the full catalog (GET /api/catalogs).
    // All setState calls are inside the async function to satisfy react-hooks/set-state-in-effect.
    const fetchBookmarkedItems = async () => {
      if (bookmarks.length === 0) {
        if (!cancelled) {
          setBookmarkedArtworks([]);
          setIsLoading(false);
        }
        return;
      }

      if (!cancelled) setIsLoading(true);

      const batchSize = 5;
      const results: (Artwork | null)[] = [];

      for (let i = 0; i < bookmarks.length; i += batchSize) {
        if (cancelled) break;
        const batch = bookmarks.slice(i, i + batchSize);
        const batchResults = await Promise.all(
          batch.map(async (id) => {
            try {
              return await getPublicCatalogItem(id);
            } catch (error) {
              if (error instanceof PrivateArtworkError) {
                // Item is private — try authenticated endpoint (own private items)
                try {
                  return await getOwnCatalogItem(id);
                } catch (innerError) {
                  logger.error('Failed to fetch private bookmarked item', { id, innerError });
                  return null;
                }
              }
              logger.error('Failed to fetch bookmarked item', { id, error });
              return null;
            }
          })
        );
        results.push(...batchResults);
      }

      if (!cancelled) {
        setBookmarkedArtworks(results.filter((item): item is Artwork => item !== null));
        setIsLoading(false);
      }
    };

    fetchBookmarkedItems();

    return () => { cancelled = true; };
  }, [bookmarks]);

  const getGridTemplate = () => {
    if (isMobile) return {
      columns: 'repeat(2, 1fr)',
      rows: 'repeat(6, 1fr)'
    };
    if (isTablet) return {
      columns: 'repeat(3, 1fr)',
      rows: 'repeat(4, 1fr)'
    };
    if (isLaptop) return {
      columns: 'repeat(4, 1fr)',
      rows: 'repeat(3, 1fr)'
    };
    return {
      columns: 'repeat(6, 1fr)',
      rows: 'repeat(2, 1fr)'
    };
  };

  const gridTemplate = getGridTemplate();

  // Determine routing: own items go to the authenticated detail page;
  // delegated items go to the authenticated route with ``?owner=`` so the
  // backend resolves to the delegator; everything else goes to public.
  const currentUserId = permissions?.userId;
  const { isDelegateOf } = useActiveCatalog();

  const getArtworkRoute = (artworkItem: Artwork) =>
    buildArtworkDetailLink(
      { artworkId: artworkItem.artworkId, userId: artworkItem.userId },
      { currentUserId: currentUserId ?? null, isDelegateOf }
    ).to;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 } }}>
      {/* Title and Buttons */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        mb: { xs: 1, sm: 2 },
        gap: 1,
        px: { xs: 1, sm: 0 }
      }}>
        <Box>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              color: theme.palette.primary.main,
              fontFamily: 'Libre Baskerville, serif',
              fontWeight: 'bold',
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' }
            }}
          >
            {en.pages.bookmarks}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{
            mt: 1,
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }}>
            Your saved artwork collection ({bookmarkedArtworks.length} items)
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<Search />}
          onClick={() => navigate('/home')}
          sx={{ textTransform: 'none' }}
        >
          Search all artwork
        </Button>
      </Box>

      {/* Grid */}
      <Box sx={{
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        height: { xs: 800, sm: 1200, md: 1000, lg: 800 },
        position: 'relative',
        display: bookmarkedArtworks.length === 0 ? 'flex' : 'grid',
        gridTemplateColumns: bookmarkedArtworks.length > 0 ? gridTemplate.columns : undefined,
        gridTemplateRows: bookmarkedArtworks.length > 0 ? gridTemplate.rows : undefined,
        alignItems: 'center',
        justifyContent: 'center',
        mx: { xs: 1, sm: 0 }
      }}>
        {bookmarkedArtworks.length === 0 && !isLoading && permissions !== null ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No bookmarked items yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start building your bookmarks collection by exploring and bookmarking artwork.
            </Typography>
            <AppButton
              variant="primary"
              onClick={() => navigate('/home')}
            >
              Explore Artwork
            </AppButton>
          </Box>
        ) : (
          <>
            {/* Horizontal dividers */}
            {Array.from({ length: (isMobile ? 5 : isTablet ? 3 : isLaptop ? 2 : 1) }).map((_, i) => (
              <Box key={`h${i}`} sx={{
                position: 'absolute',
                left: 0,
                top: `${((i + 1) / (isMobile ? 6 : isTablet ? 4 : isLaptop ? 3 : 2)) * 100}%`,
                width: '100%',
                height: '1px',
                bgcolor: theme.palette.divider
              }} />
            ))}

            {/* Vertical dividers */}
            {Array.from({ length: (isMobile ? 1 : isTablet ? 2 : isLaptop ? 3 : 5) }).map((_, i) => (
              <Box key={`v${i}`} sx={{
                position: 'absolute',
                left: `${((i + 1) / (isMobile ? 2 : isTablet ? 3 : isLaptop ? 4 : 6)) * 100}%`,
                top: 0,
                width: '1px',
                height: '100%',
                bgcolor: theme.palette.divider
              }} />
            ))}

            {/* Show bookmarked artwork */}
            {bookmarkedArtworks.map((artworkItem) => {
              const thumbnailUrl = getCardImageUrl(artworkItem);
              const title = getArtworkTitle(artworkItem);
              const creator = getCreatorDisplayName((artworkItem as { creator?: string }).creator);

              return (
                <Box
                  key={artworkItem.artworkId}
                  onClick={() => navigate(getArtworkRoute(artworkItem))}
                  sx={{
                    p: { xs: 1, sm: 1.5, md: 2 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                    '&:hover': {
                      bgcolor: theme.palette.action.hover
                    }
                  }}
                >
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleBookmark(artworkItem.artworkId);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: theme.palette.primary.main,
                      zIndex: 1
                    }}
                  >
                    <Bookmark />
                  </IconButton>

                  <Box sx={{
                    width: '100%',
                    maxWidth: '80%',
                    height: '60%',
                    flexShrink: 0
                  }}>
                    <ThumbnailImage
                      src={thumbnailUrl || null}
                      alt={title}
                    />
                  </Box>

                  <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    px: 1
                  }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontStyle: 'italic',
                        mb: 0.5,
                        fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                      }}
                    >
                      {title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mb: 0.5,
                        fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.8rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                      }}
                    >
                      {creator}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.5rem', sm: '0.6rem', md: '0.7rem' }
                      }}
                    >
                      {artworkItem.artworkId.substring(0, 8).toUpperCase()}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </>
        )}
      </Box>
    </Container>
  );
};
